// third-party
import { connectAggregationToObservable, getRows } from '@pendo/agg-connect';
import filter from 'lodash/filter';
import groupBy from 'lodash/groupBy';
import intersection from 'lodash/intersection';
import isPlainObject from 'lodash/isPlainObject';
import last from 'lodash/last';
import uniq from 'lodash/uniq';
import values from 'lodash/values';
import zip from 'lodash/zip';
import { Subject, of } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

// internal
import { FUNNEL_CHART_COLORS } from '@/common/chart/chart-colors';
import { LOADING_STATES } from '@/constants';
import { VanillaDateRange } from '@/data/vanilla-date-range';
import FunnelAggregation from '@/reports/funnel/funnel.aggregation';
import FunnelCsvAggregation from '@/reports/funnel/funnel-csv.aggregation';
import { ReportError, buildReportStream, getDefaultTimeseries, getMobileProperties, isStale } from '@/reports/report-utils';
import type { JsonObject, ReportStore, TimeSeries } from '@/reports/report-utils';
import { FilterType, MetadataTypeId } from '@/types/analytics/analytics.types';
import type { BuildProps, FunnelFilter } from '@/types/analytics/funnels.types';
import type { DXMetadataValue } from '@/types/analytics/data-explorer.types';
import moment from '@/utils/moment-wrapper';
import { isDefaultSegment, parseSegmentParams } from '@/utils/segment-utils';
import { iterateThroughMetadata } from '@/vuex/modules/metadata/metadata.utils';

export interface Grouping {
    configuration: DXMetadataValue;
    type: MetadataTypeId;
}

export interface FunnelItem {
    pageId?: string;
    featureId?: string;
    trackTypeId?: string;
    guideId?: string;
    guideStepId?: string;
    agentId?: string;
    guideEventTypes?: 'guideSeen' | 'guideActivity' | 'guideDismissed' | 'guideNotSeen';
    filters?: FunnelFilter[];
}

export interface FunnelConfig {
    items: FunnelItem[];
    appId: number[];
    segmentId: string;
    uniqueVisitorFunnel: boolean;
    maxDuration: number;
    maxInterval: number;
    mapAnonymous: boolean;
    onlyMatchedEvents: boolean;
    includeAnonymous?: boolean | string | null;
    blacklist?: string;
}

export interface FunnelHistoryEntry {
    timeSeries?: TimeSeries;
    lastSuccessRunAt?: number;
    version?: number;
}

export interface FunnelDefinition {
    type: string;
    config: FunnelConfig;
    groupings?: Grouping[];
    timeSeries: TimeSeries;
    history: FunnelHistoryEntry[];
    version: number;
}

export interface Funnel {
    id?: string;
    name?: string;
    shared?: boolean;
    type?: string;
    definition: FunnelDefinition;
    lastSuccessRunObj?: string;
}

type SavedFunnel = Funnel & { id: string };

export interface NewFunnel {
    name: string;
    shared: boolean;
    type: string;
    definition: FunnelDefinition;
}

export interface FunnelResource {
    id: string;
    name?: string;
    kind?: string;
    appId?: number;
    steps?: Array<{ id: string }>;
}

export interface AppMapEntry {
    id: number;
    displayName: string;
    platform: string;
}

export interface AppInFunnel {
    id: number;
    displayName: string;
    platform: string;
    colors: string[];
}

export interface FunnelSegment {
    id?: string;
    name?: string;
}

export interface GroupStep {
    count: number;
    steps: number;
    groupValue0?: string;
}

export interface VisitorRow {
    rowType: 'visitors';
    visitorId?: string;
    groupValue0?: string;
}

export interface GroupInfoRow {
    rowType: 'groupInfo';
    groupValue0?: string;
    groupSteps?: GroupStep[];
    averageTimes?: number[];
    medianTimes?: number[];
    averageTimeToCompletion?: number;
    medianTimeToCompletion?: number;
    percentCompleted?: number;
}

export interface WholeFunnelMetricsRow {
    rowType: 'wholeFunnelMetrics';
    percentCompleted?: number;
    averageTimeToCompletion?: number;
    medianTimeToCompletion?: number;
}

export interface FunnelExceedsCardinalityRow {
    rowType: 'funnelExceedsCardinality';
    hasExceededMaxThresholdCount?: boolean;
}

export type AggregationRow = VisitorRow | GroupInfoRow | WholeFunnelMetricsRow | FunnelExceedsCardinalityRow;

export interface FunnelGroup {
    groupValue0?: string;
    groupSteps: GroupStep[];
    averageTimes?: number[];
    medianTimes?: number[];
    averageTimeToCompletion?: number;
    medianTimeToCompletion?: number;
    percentCompleted?: number;
}

export interface PrevStep {
    appIdFromPrevious?: number;
    resourceIdFromPrevious?: string;
    resourceKindFromPrevious?: string;
    averageTimeFromPrevious?: number;
    medianTimeFromPrevious?: number;
    prevCount?: number;
}

export interface FunnelStep {
    groupByValue: string;
    resource?: FunnelResource | null;
    name: string;
    appId?: number;
    prevStep: PrevStep;
    steps: number;
    averageTimeToNextStep?: number;
    medianTimeToNextStep?: number;
    count: number;
    averageTimeToCompletion?: number;
    medianTimeToCompletion?: number;
    percentCompleted?: number;
    colorContinued?: string;
    colorDropped?: string;
    colorHover?: string;
    colorDroppedHover?: string;
    colorSelected?: string;
    colorDroppedSelected?: string;
}

export interface ProcessFunnelContext {
    appMap?: Record<string, AppMapEntry>;
    segment?: FunnelSegment;
    resources?: Array<FunnelResource | null>;
}

export interface ProcessedFunnelResponse {
    visitors: VisitorRow[];
    steps: Map<string, FunnelStep[]>;
    wholeFunnelMetrics: Partial<WholeFunnelMetricsRow>;
    segment: FunnelSegment | undefined;
    apps: AppInFunnel[];
    resources: Array<FunnelResource | null>;
    funnelExceedsCardinality: boolean;
}

interface AggConnectResponse {
    status: string;
    value: object;
    error?: Error;
}

interface FunnelTrackState {
    visitors?: Array<{ groupValue0?: string }>;
    reportStartTime?: number;
}

interface TrackFunnelReportParams {
    funnelResource: Funnel;
    funnelState: FunnelTrackState;
    trackType: string;
    location?: string;
    isAuto?: boolean;
    fromWebAnalytics?: boolean;
    fromAiAgents?: boolean;
    isImpersonating?: boolean;
    hasIntegratedMobileApps?: boolean;
    segmentName?: string;
}

interface FunnelTrackEventData {
    reportId: string | undefined;
    location: string;
    numberOfFunnelSteps: number;
    features: number;
    trackEvents: number;
    pages: number;
    guides: number;
    aiAgents: number;
    hasAIEvents: boolean;
    numberOfApps: number;
    dateRangeDays: number;
    numFilters: number;
    hasFilters: boolean;
    hasGroups: boolean;
    dateRangeSelection: string;
    segment: string;
    segmentName: string | undefined;
    analyzeBy: string;
    funnelRunType: string;
    funnelVersion: number;
    hasNonHistoricalMetadata: boolean | undefined;
    hasHistoricalMetadata: boolean | undefined;
    hasEventProperties: boolean | undefined;
    hasDeviceProps: boolean | undefined;
    hasGuideEvents: boolean;
    funnelVersionName: string;
    has_session_properties: boolean | undefined;
    from_web_analytics: boolean;
    from_ai_agents: boolean;
    includes_mobile?: boolean;
    mobile_app_name?: string;
    mobile_platform?: string;
    mobile_framework?: string;
    mobile_framework_type?: string;
    mobile_sdk_version?: string;
    mobile_plugin_version?: string;
    visitorCount?: number;
    groupCount?: number;
    funnelLoadTime?: number;
    isAuto?: boolean;
    isStale?: boolean;
}

interface CardinalityMetadata {
    id?: string;
    kind?: string;
    group?: string;
    field?: string;
    Type?: string;
    cardinalityEstimate: number;
}

interface CardinalityCheckParams {
    groupings?: Grouping[];
    metadata: Record<string, CardinalityMetadata[]>;
    historicalMetadata: CardinalityMetadata[];
}

export const TABLE_TYPES = {
    VISITOR: 'Visitor',
    SUMMARY: 'Summary',
    STEP: 'Step',
};

export const VISITOR_ROW_LIMIT = 10000;

export const MAX_CARDINALITY_LIMIT = 10000;

export const FUNNEL_PARAMETERS_KEY = '_pendo_funnel_parameters';

export function buildAggParams (funnel: Funnel): BuildProps {
    const { definition } = funnel;
    const { config, groupings } = definition;
    const vanillaDateRange = new VanillaDateRange(definition.timeSeries);
    const timeSeries = vanillaDateRange.timeSeries();

    return {
        config: parseSegmentParams(config) as object as BuildProps['config'],
        timeSeries: {
            period: timeSeries.period,
            first: timeSeries.first,
            count: timeSeries.count,
        },
        groupings,
    };
}

export function compileAggregation (funnel: Funnel) {
    const params = buildAggParams(funnel);

    return FunnelAggregation.build(params);
}

export function validate (_funnel: Funnel, { resources }: ProcessFunnelContext = {}): void {
    const runnable = Array.isArray(resources) && resources.every(Boolean);
    if (!runnable) {
        throw new ReportError({
            message:
                'We are unable to run your funnel because we could not find some of the pages, features, or track events. Please edit the funnel and correct the missing pages, features, or track events, or delete the funnel if you no longer need it.',
            retriable: false,
        });
    }
}

export function buildTimeSeriesString (timeSeries: TimeSeries, preposition?: boolean): string {
    let dateRangeString = '';
    const { start, end, type } = timeSeries;
    const isOneDay = moment(start).isSame(moment(end).subtract(1, 'days'), 'day');
    if (type !== 'Custom') {
        dateRangeString = type.toLowerCase();
        if (type !== 'Today' && type !== 'Yesterday') {
            dateRangeString = preposition ? `over ${dateRangeString}` : dateRangeString;
        }
    } else if (isOneDay) {
        if (moment().isSame(moment(start), 'day')) {
            dateRangeString = 'today';
        } else if (
            moment()
                .subtract(1, 'days')
                .isSame(moment(start), 'day')
        ) {
            dateRangeString = 'yesterday';
        } else {
            dateRangeString = preposition
                ? `on ${moment(start, { ignoreTimeZone: true }).format('ll')}`
                : moment(start, { ignoreTimeZone: true }).format('ll');
        }
    } else {
        dateRangeString = preposition
            ? `over ${moment(start, { ignoreTimeZone: true }).format('ll')} - ${moment(end, {
                ignoreTimeZone: true,
            })
                .subtract(1, 'days')
                .format('ll')}`
            : `${moment(start, { ignoreTimeZone: true }).format('ll')} - ${moment(end, {
                ignoreTimeZone: true,
            })
                .subtract(1, 'days')
                .format('ll')}`;
    }

    return dateRangeString;
}

export function buildResolvedFunnelState (
    uniqueVisitorFunnel: boolean,
    savedVisibleGroups: string[] | undefined,
    value: ProcessedFunnelResponse,
    status: string,
    tableBreakdown: string = '',
) {
    return {
        status,
        percentComplete: 100,
        steps: value.steps,
        isShowingDropoff: false,
        apps: value.apps,
        segment: value.segment || {},
        visitors: Object.freeze(value.visitors),
        wholeFunnelMetrics: value.wholeFunnelMetrics,
        funnelType: uniqueVisitorFunnel ? 'uniqueVisitors' : 'totalAttempts',
        selectedStep: undefined,
        visibleGroups: savedVisibleGroups
            ? intersection(savedVisibleGroups, [...value.steps.keys()])
            : [...value.steps.keys()],
        selectedGroup: undefined,
        resources: value.resources,
        sessionReplay: undefined,
        tableBreakdown,
        funnelExceedsCardinality: value.funnelExceedsCardinality,
    };
}

function getAppsInFunnel (
    resources: Array<FunnelResource | null>,
    appMap: Record<string, AppMapEntry>,
): Record<string, AppInFunnel> {
    const colorsContinued = [...FUNNEL_CHART_COLORS.continued];
    let numOfApps = 0;
    const appsInFunnel: Record<string, AppInFunnel> = {};
    resources.forEach((resource) => {
        const appId = resource?.appId;
        if (appId != null && !appsInFunnel[appId] && appMap[appId]) {
            const app = appMap[appId];
            const appColor = colorsContinued[numOfApps % colorsContinued.length];
            appsInFunnel[appId] = {
                id: appId,
                displayName: app.displayName,
                platform: app.platform,
                colors: [appColor],
            };
            numOfApps++;
        }
    });

    return appsInFunnel;
}

function fillIncompleteFunnel (incompleteStepsList: GroupStep[]): GroupStep[] {
    // if n steps have identical `count` vals then
    // the result only contains the last
    // so we need to build out a list of all steps
    const lastStep = last(incompleteStepsList);
    const allSteps: GroupStep[] = [];
    let curIndex = incompleteStepsList.length - 2;
    if (lastStep) {
        allSteps.push(lastStep);
        for (let i = lastStep.steps - 1; i > 0; i--) {
            if (curIndex > -1 && incompleteStepsList[curIndex].steps === i) {
                allSteps.unshift(incompleteStepsList[curIndex]);
                curIndex--;
            } else {
                allSteps.unshift({
                    count: incompleteStepsList[curIndex + 1].count,
                    steps: i,
                    groupValue0: incompleteStepsList[curIndex + 1].groupValue0,
                });
            }
        }
    }

    return allSteps;
}

function buildGroup (
    funnel: Funnel,
    resources: Array<FunnelResource | null>,
    group: FunnelGroup,
    groupByValue: string = 'ungrouped',
): FunnelStep[] {
    const { items } = funnel.definition.config;
    const steps = group.groupSteps;

    return zip(steps, resources, items).map(([step, resource, item], i) => {
        let prevStep: PrevStep = {};
        if (i !== 0) {
            const prev = steps[i - 1];
            const prevResource = resources[i - 1];
            prevStep = {
                appIdFromPrevious: prevResource?.appId,
                resourceIdFromPrevious: prevResource?.id,
                resourceKindFromPrevious: prevResource?.kind,
                averageTimeFromPrevious: group.averageTimes?.[i - 1],
                medianTimeFromPrevious: group.medianTimes?.[i - 1],
                prevCount: prev?.count || 0,
            };
        }

        let name: string = resource?.name ?? 'Unknown step';
        if (item?.guideEventTypes) {
            const guideSteps = resource?.steps ?? [];
            const guideStepIndex = guideSteps.findIndex((guideStep) => guideStep.id === item.guideStepId);
            switch (item.guideEventTypes) {
                case 'guideSeen':
                    name += item.guideStepId ? ` - Seen at Step ${guideStepIndex + 1}` : ' - Seen at any step';
                    break;
                case 'guideDismissed':
                    name += item.guideStepId
                        ? ` - Dismissed at Step ${guideStepIndex + 1}`
                        : ' - Dismissed at any step';
                    break;
                case 'guideNotSeen':
                    name += ' - Not seen (control group)';
            }
        }

        const config: FunnelStep = {
            groupByValue,
            resource,
            name,
            appId: resource?.appId,
            prevStep,
            steps: i + 1,
            averageTimeToNextStep: group.averageTimes?.[i],
            medianTimeToNextStep: group.medianTimes?.[i],
            count: step?.count || 0,
            averageTimeToCompletion: group.averageTimeToCompletion,
            medianTimeToCompletion: group.medianTimeToCompletion,
            percentCompleted: group.percentCompleted,
        };

        return config;
    });
}

export function processResponse (
    funnel: Funnel,
    rows: JsonObject[],
    { appMap = {}, segment, resources = [] }: ProcessFunnelContext = {},
): ProcessedFunnelResponse {
    const aggRows = rows as object[] as AggregationRow[];
    const groupRows = groupBy(aggRows, ({ rowType }) => rowType);
    const visitors = (groupRows.visitors ?? []) as VisitorRow[];
    const wholeFunnelMetrics = (groupRows.wholeFunnelMetrics ?? []) as WholeFunnelMetricsRow[];
    const groupInfo = (groupRows.groupInfo ?? []) as GroupInfoRow[];
    const funnelExceedsCardinality = (groupRows.funnelExceedsCardinality ?? []) as FunnelExceedsCardinalityRow[];
    const appsInFunnel = getAppsInFunnel(resources, appMap);

    const groupInfoMap = new Map<string, FunnelStep[]>();
    for (const group of groupInfo) {
        const key = group.groupValue0 || 'ungrouped';
        const savedGroupByKey = groupInfoMap.has(key);

        const groupSteps = group.groupSteps || [];
        const sortedGroupSteps = groupSteps.sort((stepA, stepB) => stepA.steps - stepB.steps);
        const completedGroupSteps = fillIncompleteFunnel(sortedGroupSteps);

        const fixedGroup: FunnelGroup = {
            ...group,
            groupSteps: completedGroupSteps,
        };

        const builtGroup = buildGroup(funnel, resources, fixedGroup, key);

        if (!savedGroupByKey) {
            groupInfoMap.set(key, builtGroup);
        }
    }

    const groupInfoArray = Array.from(groupInfoMap);
    // Assign colors to steps
    const colorsContinued = [...FUNNEL_CHART_COLORS.continued];
    const colorsDropped = [...FUNNEL_CHART_COLORS.dropped];
    const colorsHover = [...FUNNEL_CHART_COLORS.hover];
    const colorsDroppedHover = [...FUNNEL_CHART_COLORS.droppedHover];
    const colorsSelected = [...FUNNEL_CHART_COLORS.selected];
    const colorsDroppedSelected = [...FUNNEL_CHART_COLORS.droppedSelected];
    if (funnel.definition.groupings?.length) {
        groupInfoArray.forEach((stepGroup, idx) => {
            stepGroup[1].forEach((step) => {
                step.colorContinued = colorsContinued[idx % colorsContinued.length];
                step.colorDropped = colorsDropped[idx % colorsDropped.length];
                step.colorHover = colorsHover[idx % colorsHover.length];
                step.colorDroppedHover = colorsDroppedHover[idx % colorsDroppedHover.length];
                step.colorSelected = colorsSelected[idx % colorsSelected.length];
                step.colorDroppedSelected = colorsDroppedSelected[idx % colorsDroppedSelected.length];
            });
        });
    } else {
        const appIds = Object.keys(appsInFunnel);
        (groupInfoArray[0]?.[1] ?? []).forEach((step) => {
            const idx = appIds.findIndex((id) => id === step.appId?.toString());
            step.colorContinued = colorsContinued[idx % colorsContinued.length];
            step.colorDropped = colorsDropped[idx % colorsDropped.length];
            step.colorHover = colorsHover[idx % colorsHover.length];
            step.colorDroppedHover = colorsDroppedHover[idx % colorsDroppedHover.length];
            step.colorSelected = colorsSelected[idx % colorsSelected.length];
            step.colorDroppedSelected = colorsDroppedSelected[idx % colorsDroppedSelected.length];
        });
    }

    return {
        visitors,
        steps: new Map(groupInfoArray),
        wholeFunnelMetrics: wholeFunnelMetrics[0] || {},
        segment,
        apps: values(appsInFunnel),
        resources,
        funnelExceedsCardinality: funnelExceedsCardinality[0]?.hasExceededMaxThresholdCount ?? false,
    };
}

export function createFunnelStream (
    resolveFunnelData: (funnel: Funnel) => ProcessFunnelContext,
    reportStore: ReportStore<SavedFunnel>,
    ensureResourcesLoaded: (funnel: Funnel) => Promise<void>,
) {
    return buildReportStream<SavedFunnel, BuildProps, ProcessedFunnelResponse>(
        {
            aggregation: FunnelAggregation,
            validate: async (funnel) => {
                await ensureResourcesLoaded(funnel);

                return validate(funnel, resolveFunnelData(funnel));
            },
            buildAggParams,
            processResponse: (funnel, rows) => processResponse(funnel, rows, resolveFunnelData(funnel)),
        },
        reportStore,
    );
}

export function createUnsavedFunnelStream (funnel: Funnel, resolveFunnelData: (funnel: Funnel) => ProcessFunnelContext) {
    const subject = new Subject<[Funnel]>();
    const aggregation = FunnelAggregation as Partial<typeof FunnelAggregation>;
    delete aggregation.requestId;
    delete aggregation.response;
    const response$ = connectAggregationToObservable(
        aggregation,
        subject.pipe(
            mergeMap(async ([streamedFunnel]) => {
                return {
                    ...buildAggParams(streamedFunnel),
                };
            }),
        ),
        undefined,
    ).pipe(
        mergeMap(async (response: AggConnectResponse) => {
            const { LOADING, NOTIFICATION, REJECTED, RESOLVED } = LOADING_STATES;
            let data;
            switch (response.status) {
                case LOADING:
                    return response;
                case NOTIFICATION:
                    return response;
                case REJECTED:
                    return response.error;
                case RESOLVED:
                    data = getRows(response.value);

                    return {
                        status: response.status,
                        value: processResponse(funnel, data, resolveFunnelData(funnel)),
                    };
            }

            return undefined;
        }),
    );

    return [response$, subject];
}

export function fetchCsv (funnel: Funnel, { resources }: ProcessFunnelContext = {}) {
    return connectAggregationToObservable(
        FunnelCsvAggregation,
        of({
            gcsFileName: funnel.lastSuccessRunObj,
            resources,
        }),
        undefined,
    );
}

// These funnel versions indicate which features are currently in use
export const FUNNEL_VERSION = 5;

export const FUNNEL_VERSIONS: Record<number, string> = {
    0: 'classicFunnel',
    1: 'wholeFunnel',
    2: 'uniqueVisitorFunnel',
    3: 'groupByFunnel',
    4: 'sessionReplayFunnel',
    5: 'aggGroupInfoFunnel',
};

export function getFunnelVersion (): { version: number; versionName: string } {
    const version = FUNNEL_VERSION;

    return {
        version,
        versionName: FUNNEL_VERSIONS[version],
    };
}

export function checkValidFilterType (items: FunnelItem[], validTypes: FilterType[]): boolean {
    return items.some((item) => item.filters?.some(({ filterType }) => validTypes.includes(filterType)));
}

export function checkValidGroupType (
    groupings: Grouping[] | undefined,
    validTypes: MetadataTypeId[],
): boolean | undefined {
    return groupings?.some(({ type, configuration }) => {
        if (type === MetadataTypeId.Event) {
            return validTypes.includes(configuration.kind);
        }

        return validTypes.includes(type);
    });
}

export function trackFunnelReport ({
    funnelResource,
    funnelState,
    trackType,
    location = 'report',
    isAuto = false,
    fromWebAnalytics = false,
    fromAiAgents = false,
    isImpersonating = false,
    hasIntegratedMobileApps = false,
    segmentName,
}: TrackFunnelReportParams): void {
    const { definition, id } = funnelResource;
    const { config, version, timeSeries, groupings } = definition;
    const { items, appId, segmentId, uniqueVisitorFunnel } = config;

    const dateRangeStart = moment(timeSeries.start);
    const dateRangeEnd = moment(timeSeries.end);
    const dateRangeDays = dateRangeEnd.diff(dateRangeStart, 'days');

    const numFilters = items.reduce((count, item) => {
        return count + (item.filters?.length ?? 0);
    }, 0);

    const hasNonHistoricalMetadata = checkValidGroupType(groupings, [
        MetadataTypeId.Visitor,
        MetadataTypeId.Account,
        MetadataTypeId.ParentAccount,
    ]);
    const hasHistoricalMetadata =
        checkValidFilterType(items, [FilterType.HistoricalMetadata]) ||
        checkValidGroupType(groupings, [MetadataTypeId.Historical]);
    const hasEventProperties =
        checkValidFilterType(items, [FilterType.Custom]) || checkValidGroupType(groupings, [MetadataTypeId.Event]);
    const hasDeviceProps =
        checkValidFilterType(items, [FilterType.Device]) || checkValidGroupType(groupings, [MetadataTypeId.Automatic]);
    const hasGuideEvents = Boolean(filter(items, 'guideId').length);

    const appIds = appId.map((singleAppId) => {
        return { appId: singleAppId };
    });
    const trackMobileProperties = getMobileProperties(appIds, { hasIntegratedMobileApps });

    const hasSessionPropertiesFilters = items?.some((item) =>
        item.filters?.some((funnelFilter) => funnelFilter.filterType === FilterType.Utm),
    );

    const hasSessionPropertiesGroups = groupings?.some((grouping) => grouping.type === MetadataTypeId.Utm);

    let trackEventData: FunnelTrackEventData = {
        reportId: id,
        location,
        numberOfFunnelSteps: items.length,
        features: filter(items, 'featureId').length,
        trackEvents: filter(items, 'trackTypeId').length,
        pages: filter(items, 'pageId').length,
        guides: filter(items, 'guideId').length,
        aiAgents: filter(items, 'agentId').length,
        hasAIEvents: filter(items, 'agentId').length > 0,
        numberOfApps: appId.length,
        dateRangeDays,
        numFilters,
        hasFilters: numFilters > 0,
        hasGroups: (groupings?.length ?? 0) > 0,
        dateRangeSelection: timeSeries.type,
        segment: isDefaultSegment(segmentId) ? 'default' : 'custom',
        segmentName,
        analyzeBy: uniqueVisitorFunnel ? 'uniqueVisitors' : 'totalAttempts',
        funnelRunType: trackType,
        funnelVersion: version,
        hasNonHistoricalMetadata,
        hasHistoricalMetadata,
        hasEventProperties,
        hasDeviceProps,
        hasGuideEvents,
        funnelVersionName: FUNNEL_VERSIONS[version],
        ...trackMobileProperties,
        has_session_properties: hasSessionPropertiesFilters || hasSessionPropertiesGroups,
        from_web_analytics: fromWebAnalytics,
        from_ai_agents: fromAiAgents,
    };

    if (trackType === 'resolved') {
        const { visitors = [], reportStartTime = 0 } = funnelState;
        const uniqueGroups = uniq(visitors.map(({ groupValue0 }) => groupValue0));

        trackEventData = {
            ...trackEventData,
            visitorCount: visitors.length,
            groupCount: uniqueGroups.length,
            funnelLoadTime: Date.now() - reportStartTime,
        };

        window.pendo.track('Funnel Report Complete', trackEventData);
    } else {
        trackEventData = {
            ...trackEventData,
            isAuto,
            isStale: isAuto && trackType !== 'run' ? isStale(funnelResource as SavedFunnel, { isImpersonating }) : false,
        };

        window.pendo.track('Run Funnel Report', trackEventData);
    }
}

export function buildDefaultFunnel (): NewFunnel {
    return {
        name: 'New Funnel',
        shared: true,
        type: 'Funnel',
        definition: {
            type: 'Funnel', // keeping for backwards compat with old funnels code
            config: {
                maxDuration: 0,
                maxInterval: 0,
                items: [{}, {}],
                appId: [],
                uniqueVisitorFunnel: true,
                segmentId: 'everyone',
                mapAnonymous: true,
                onlyMatchedEvents: true,
            },
            groupings: [],
            timeSeries: getDefaultTimeseries(),
            history: [],
            version: getFunnelVersion().version,
        },
    };
}

export function ensureCompleteFunnelConfig (funnelConfig: FunnelDefinition): FunnelDefinition {
    const { definition: defaultFunnelConfig } = buildDefaultFunnel();

    const iterate = (iterateObj: object, comparisonObj: object): object => {
        const comparisonObjCopy = comparisonObj;
        Object.keys(iterateObj).forEach((key) => {
            const iterateValue = Reflect.get(iterateObj, key);
            // Check for matches
            if (Object.hasOwn(comparisonObjCopy, key)) {
                // If the key is an object value, iterate through that one first
                if (isPlainObject(iterateValue)) {
                    const completedObject = iterate(iterateValue, Reflect.get(comparisonObjCopy, key));
                    Reflect.set(comparisonObjCopy, key, completedObject);
                }

                return;
            }

            // If no matches, we need to initialize the value with the default config's
            Reflect.set(comparisonObjCopy, key, iterateValue);
        });

        return comparisonObjCopy;
    };

    const completeFunnelConfig = iterate(defaultFunnelConfig, funnelConfig);

    return completeFunnelConfig as FunnelDefinition;
}

export function checkIfGroupingsExceedCardinality ({
    groupings,
    metadata,
    historicalMetadata,
}: CardinalityCheckParams): boolean {
    if (groupings?.length) {
        return groupings.some(({ configuration, type }) => {
            const { kind, group, field } = configuration;
            const groupingId = `${kind}.${group}.${field}`;

            if (type === MetadataTypeId.Historical) {
                return historicalMetadata.some(
                    ({ id, kind: Kind, group: Group, field: Field, cardinalityEstimate }) => {
                        const metadataId = id || `${Kind}.${Group}.${Field}`;

                        return metadataId === groupingId && cardinalityEstimate > MAX_CARDINALITY_LIMIT;
                    },
                );
            }

            if (type === MetadataTypeId.Event && kind !== MetadataTypeId.Automatic) {
                const metadataByType = metadata[type] ?? [];
                const metadataList: CardinalityMetadata[] = iterateThroughMetadata(metadataByType, type);
                const validMetadataTypes = ['string', 'integer', 'float'];

                return metadataList.some(
                    ({ id, kind: Kind, group: Group, field: Field, Type, cardinalityEstimate }) => {
                        const metadataId = id || `${Kind}.${Group}.${Field}`;

                        return (
                            Group !== 'pendo' &&
                            validMetadataTypes.includes(Type ?? '') &&
                            metadataId === groupingId &&
                            cardinalityEstimate > MAX_CARDINALITY_LIMIT
                        );
                    },
                );
            }

            return false;
        });
    }

    return false;
}
